import React, { useState, useEffect, useCallback, useRef } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import axios from 'axios';
import Loading from './Loading';
import ExhibitorListingItem from './ExhibitorListingItem';

const FinderResults = ({
  conventionId,
  search,
  searchFilter,
  isApiReady,
  onExhibitorSelect,
  selectedExhibitor,
}) => {
  const [totalExhibitors, setTotalExhibitors] = useState(0);
  const [exhibitors, setExhibitors] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const loadingRef = useRef(false);
  const cancelTokenRef = useRef(null);
  const initialLoadRef = useRef(false);

  // Reset state whenever filters change
  useEffect(() => {
    resetState();
    initialLoadRef.current = false;
  }, [conventionId, search, searchFilter]);

  const resetState = useCallback(() => {
    // Cancel any pending requests
    if (cancelTokenRef.current) {
      cancelTokenRef.current.cancel('Request cancelled due to filter change');
    }

    setTotalExhibitors(0);
    setExhibitors([]);
    setHasMore(true);
    setLoading(false);
    setError(null);
    setCurrentPage(1);
    loadingRef.current = false;
  }, []);

  const loadExhibitors = useCallback(
    async (page = 1, reset = false) => {
      // Prevent multiple simultaneous requests
      if (!isApiReady || loadingRef.current) {
        return;
      }

      loadingRef.current = true;
      setLoading(true);
      setError(null);

      // Cancel previous request if it exists
      if (cancelTokenRef.current) {
        cancelTokenRef.current.cancel('Request cancelled');
      }

      // Create new cancel token
      const cancelToken = axios.CancelToken.source();
      cancelTokenRef.current = cancelToken;

      try {
        const params = {
          c: conventionId,
          page: page,
          per_page: 25,
        };

        if (search) {
          params.search = search;
        }

        if (
          searchFilter.exhibitor_type &&
          searchFilter.exhibitor_type.length > 0
        ) {
          params.exhibitor_type = searchFilter.exhibitor_type;
        }

        if (searchFilter.tags && searchFilter.tags.length > 0) {
          params.tags = searchFilter.tags;
        }

        const response = await axios.get('/api/v1/exhibitor_profiles', {
          params,
          cancelToken: cancelToken.token,
        });

        const newExhibitors = response.data.exhibitors || [];
        const meta = response.data.meta || {};

        if (!cancelToken.token.reason) {
          if (reset || page === 1) {
            setExhibitors(newExhibitors);
          } else {
            setExhibitors(prev => [...prev, ...newExhibitors]);
          }

          setTotalExhibitors(meta.totalCount || 0);
          setHasMore(meta.currentPage < meta.totalPages);
          setCurrentPage(meta.currentPage || 1);
        }
      } catch (err) {
        if (!axios.isCancel(err)) {
          console.error('Error loading exhibitors:', err);
          setError(err.response?.data?.error || 'Failed to load exhibitors');
          setHasMore(false);
        }
      } finally {
        loadingRef.current = false;
        setLoading(false);
        if (cancelTokenRef.current === cancelToken) {
          cancelTokenRef.current = null;
        }
      }
    },
    [isApiReady, conventionId, search, searchFilter],
  );

  // Load initial data when API is ready or filters change
  useEffect(() => {
    if (isApiReady && !initialLoadRef.current) {
      initialLoadRef.current = true;
      loadExhibitors(1, true);
    }
  }, [isApiReady, search, searchFilter, loadExhibitors]);

  const loadMoreExhibitors = useCallback(() => {
    if (hasMore && !loadingRef.current && !loading) {
      loadExhibitors(currentPage + 1, false);
    }
  }, [hasMore, currentPage, loadExhibitors, loading]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (cancelTokenRef.current) {
        cancelTokenRef.current.cancel('Component unmounted');
      }
    };
  }, []);

  const resultsMessage = totalExhibitors === 1 ? 'exhibitor' : 'exhibitors';

  if (error) {
    return (
      <div className="exhibitor-error">
        <h3>Error Loading Exhibitors</h3>
        <p>{error}</p>
        <button onClick={() => loadExhibitors(1, true)} className="button">
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="exhibitor-results" style={{ padding: '0' }}>
      <div
        className="results-summary"
        style={{
          padding: '5px 0',
          margin: '0',
          fontSize: '0.9em',
        }}
      >
        <p
          style={{ margin: '0' }}
        >{`Listing ${totalExhibitors} ${resultsMessage}`}</p>
      </div>

      <div className="results-container" style={{ padding: '0' }}>
        <InfiniteScroll
          dataLength={exhibitors.length}
          next={loadMoreExhibitors}
          hasMore={hasMore && !loading}
          loader={<Loading />}
          scrollThreshold={0.8}
        >
          <div className="exhibitor-grid" style={{ margin: '0', padding: '0' }}>
            {exhibitors.map(exhibitor => (
              <ExhibitorListingItem
                key={exhibitor.profileId || exhibitor.id}
                exhibitor={exhibitor}
                onSelect={onExhibitorSelect}
                isSelected={
                  selectedExhibitor && selectedExhibitor.id === exhibitor.id
                }
                aggregated={true}
              />
            ))}
          </div>
        </InfiniteScroll>

        {exhibitors.length === 0 && !loading && initialLoadRef.current && (
          <div className="no-results">
            <h3>No Exhibitors Found</h3>
            <p>Try adjusting your search criteria or filters.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default FinderResults;
